import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { FileText, Clock, Wrench, Download, Search } from 'lucide-react';
type PageView = 'home' | 'archive' | 'detail' | 'configurator' | 'warranty' | 'instructions' | 'contact';
interface InstructionsPageProps {
  onNavigate: (page: PageView, productId?: number) => void;
}
interface Guide {
  name: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  time: string;
  sku: string;
  tools: string;
}
const guides: Guide[] = [{
  name: '6" Pro-Stage 1 Lift Kit System',
  difficulty: 'Advanced',
  time: '6-8 hrs',
  sku: 'POD-LK-F150-06',
  tools: 'Full tool set, spring compressor, torque wrench'
}, {
  name: '4" Performance Lift System',
  difficulty: 'Advanced',
  time: '5-7 hrs',
  sku: 'POD-LK-TACO-04',
  tools: 'Full tool set, spring compressor, torque wrench'
}, {
  name: '2.5" Leveling Kit',
  difficulty: 'Beginner',
  time: '2-3 hrs',
  sku: 'POD-LVL-F150',
  tools: 'Basic hand tools, floor jack, jack stands'
}, {
  name: 'Billet Upper Control Arms',
  difficulty: 'Intermediate',
  time: '2-3 hrs',
  sku: 'POD-UCA-GM-1500',
  tools: 'Ball joint press, torque wrench, basic hand tools'
}, {
  name: 'Dual Steering Stabilizer Kit',
  difficulty: 'Beginner',
  time: '1-2 hrs',
  sku: 'POD-SS-RAM-HD',
  tools: 'Basic hand tools, torque wrench'
}, {
  name: 'Adjustable Track Bar',
  difficulty: 'Intermediate',
  time: '1-2 hrs',
  sku: 'POD-TB-JK-FR',
  tools: 'Basic hand tools, tape measure, torque wrench'
}, {
  name: 'Rear Leaf Spring Pack',
  difficulty: 'Intermediate',
  time: '3-4 hrs',
  sku: 'POD-LS-TACO-RR',
  tools: 'Spring clamps, floor jack, torque wrench'
}, {
  name: 'Front Coilover Conversion',
  difficulty: 'Advanced',
  time: '4-6 hrs',
  sku: 'POD-CO-F150-25',
  tools: 'Spring compressor, torque wrench, full tool set'
}, {
  name: 'Differential Cover Guard',
  difficulty: 'Beginner',
  time: '30 min',
  sku: 'POD-DIFF-DANA44',
  tools: 'Socket set, torque wrench'
}];
const difficultyColor = (d: string) => {
  if (d === 'Beginner') return 'bg-green-100 text-green-700';
  if (d === 'Intermediate') return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
};
export function InstructionsPage({
  onNavigate
}: InstructionsPageProps) {
  const [filter, setFilter] = useState<string>('All');
  const [search, setSearch] = useState('');
  const filtered = guides.filter(g => {
    const matchesDifficulty = filter === 'All' || g.difficulty === filter;
    const matchesSearch = g.name.toLowerCase().includes(search.toLowerCase());
    return matchesDifficulty && matchesSearch;
  });
  return <div className="min-h-screen flex flex-col bg-background font-sans" data-id="element-465">
      <Header data-id="element-466" />
      <Navigation onNavigate={onNavigate} data-id="element-467" />

      <main className="flex-grow" data-id="element-468">
        {/* Hero */}
        <div className="bg-charcoal text-white py-16 relative overflow-hidden" data-id="element-469">
          <div className="absolute inset-0 bg-hero-pattern opacity-10 bg-cover bg-center" data-id="element-470" />
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary" data-id="element-471" />
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 relative z-10" data-id="element-472">
            <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-473">
              <button onClick={() => onNavigate('home')} className="hover:text-primary transition-colors" data-id="element-474">
                Home
              </button>
              <span data-id="element-475">/</span>
              <span className="text-white" data-id="element-476">Instructions</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-heading font-bold uppercase italic tracking-tighter" data-id="element-477">
              Installation <span className="text-primary" data-id="element-478">Guides</span>
            </h1>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16" data-id="element-479">
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mb-10" data-id="element-480">
            Proper installation is critical to the performance and safety of
            your suspension components. Download the guide for your specific
            product and follow each step carefully. When in doubt, consult a
            qualified technician.
          </p>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-481">
            <div className="relative flex-grow max-w-md" data-id="element-482">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-483" />
              <input type="text" placeholder="Search guides..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 bg-white text-sm focus:outline-none focus:border-primary transition-colors" data-id="element-484" />
            </div>
            <div className="flex gap-2" data-id="element-485">
              {['All', 'Beginner', 'Intermediate', 'Advanced'].map(level => <button key={level} onClick={() => setFilter(level)} className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors border
                    ${filter === level ? 'bg-charcoal text-white border-charcoal' : 'bg-white text-gray-500 border-gray-200 hover:border-charcoal hover:text-charcoal'}`} data-id="element-486">
                  {level}
                </button>)}
            </div>
          </div>

          {/* Guides Grid */}
          {filtered.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-487">
              {filtered.map(guide => <div key={guide.sku} className="bg-white border border-gray-200 flex flex-col" data-id="element-488">
                  <div className="p-6 flex-grow" data-id="element-489">
                    <div className="flex items-start justify-between gap-3 mb-4" data-id="element-490">
                      <FileText className="w-8 h-8 text-primary flex-shrink-0" data-id="element-491" />
                      <span className={`text-xs font-bold uppercase px-2 py-1 ${difficultyColor(guide.difficulty)}`} data-id="element-492">
                        {guide.difficulty}
                      </span>
                    </div>
                    <h3 className="text-xl font-heading font-bold text-charcoal uppercase italic mb-3 leading-tight" data-id="element-493">
                      {guide.name}
                    </h3>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-4" data-id="element-494">
                      SKU: {guide.sku}
                    </p>
                    <div className="space-y-2 text-sm text-gray-600" data-id="element-495">
                      <div className="flex items-center gap-2" data-id="element-496">
                        <Clock className="w-4 h-4 text-primary" data-id="element-497" />
                        <span data-id="element-498">
                          Est. Time: <strong data-id="element-499">{guide.time}</strong>
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-500">
                        <Wrench className="w-4 h-4 text-primary" data-id="element-501" />
                        <span data-id="element-502">{guide.tools}</span>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-100 p-4" data-id="element-503">
                    <button className="w-full flex items-center justify-center gap-2 py-3 bg-charcoal text-white font-heading font-bold uppercase italic tracking-wider hover:bg-primary transition-colors text-sm" data-id="element-504">
                      <Download className="w-4 h-4" data-id="element-505" /> Download PDF
                    </button>
                  </div>
                </div>)}
            </div> : <div className="text-center py-16 bg-white border border-dashed border-gray-300" data-id="element-506">
              <p className="text-gray-400 font-heading font-bold text-xl uppercase italic" data-id="element-507">
                No guides match your search
              </p>
              <button onClick={() => {
            setSearch('');
            setFilter('All');
          }} className="mt-3 text-primary font-bold hover:underline text-sm" data-id="element-508">
                Clear Filters
              </button>
            </div>}

          {/* Help Banner */}
          <div className="mt-16 bg-charcoal text-white p-8 flex flex-col md:flex-row items-center justify-between gap-6 border-t-4 border-primary" data-id="element-509">
            <div data-id="element-510">
              <h3 className="text-2xl font-heading font-bold uppercase italic mb-1" data-id="element-511">
                Need Installation Help?
              </h3>
              <p className="text-gray-400 text-sm" data-id="element-512">
                Our tech support team can walk you through any step over the
                phone.
              </p>
            </div>
            <button onClick={() => onNavigate('contact')} className="px-8 py-3 bg-primary text-white font-heading font-bold uppercase italic tracking-wider hover:bg-white hover:text-charcoal transition-colors skew-x-[-10deg] flex-shrink-0" data-id="element-513">
              <span className="block skew-x-[10deg]" data-id="element-514">Contact Support</span>
            </button>
          </div>
        </div>
      </main>

      <Footer data-id="element-515" />
    </div>;
}